import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { API_URL } from '../utils/api';

// OLSO×Simphony demo against Burger King point 998.
//  • Scenario A: order opened on the POS → QR on display → upsell from phone → POS.
//  • Scenario B: order built in OLSO → pushed to the POS as a check.
//  • A ?check=<ref> URL is the phone/customer view of an existing check.

interface CheckLine { menuItemId: number; name: string; quantity: number; total: number }
interface Check { checkRef: string; checkNumber: number; status: string; items: CheckLine[]; tax: number; total: number }
interface MenuItem { menuItemId: number; name: string; price: number }

const card: React.CSSProperties = { border: '1px solid #e6ddcf', borderRadius: 14, padding: 16, background: '#fff' };
const btn: React.CSSProperties = { padding: '12px 16px', borderRadius: 12, border: 'none', fontWeight: 700, cursor: 'pointer' };
const wrap: React.CSSProperties = { maxWidth: 560, margin: '0 auto', padding: 20, fontFamily: 'system-ui, sans-serif', color: '#2a2118' };

function CheckCard({ check }: { check: Check }) {
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}>Чек №{check.checkNumber} в кассе BK · статус {check.status}</div>
      {check.items.map((i, idx) => (
        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0e9dd' }}>
          <span>{i.quantity}× {i.name}</span><strong>₸{i.total}</strong>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, opacity: 0.7, fontSize: 13 }}><span>в т.ч. НДС</span><span>₸{check.tax}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, fontSize: 17 }}><strong>Итого</strong><strong>₸{check.total}</strong></div>
    </div>
  );
}

function UpsellButtons({ checkRef, restaurantId, options, onUpdate }: { checkRef: string; restaurantId: string; options: MenuItem[]; onUpdate: (c: Check) => void }) {
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState<number[]>([]);
  const [err, setErr] = useState('');
  const add = async (o: MenuItem) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/simphony-demo/${checkRef}/upsell`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId, menuItemId: o.menuItemId }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Апсел не прошёл');
      onUpdate(d.check); setAdded((a) => [...a, o.menuItemId]);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <>
      <div style={{ fontWeight: 700, margin: '8px 0' }}>Докупить — упадёт в их кассу:</div>
      {err && <div style={{ color: '#b23', marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {options.map((o) => (
          <button key={o.menuItemId} style={{ ...card, ...btn, background: added.includes(o.menuItemId) ? '#eef7ee' : '#fff8ef', textAlign: 'left' }} disabled={busy} onClick={() => add(o)}>
            <div style={{ fontWeight: 700 }}>{o.name}</div>
            <div style={{ opacity: 0.7 }}>₸{o.price} {added.includes(o.menuItemId) ? '✓' : '+'}</div>
          </button>
        ))}
      </div>
    </>
  );
}

// Phone/customer view opened from the QR: ?check=<ref>
function CustomerView({ checkRef, restaurantId }: { checkRef: string; restaurantId: string }) {
  const [check, setCheck] = useState<Check | null>(null);
  const [options, setOptions] = useState<MenuItem[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    fetch(`${API_URL}/api/simphony-demo/check/${checkRef}?restaurantId=${restaurantId}`).then((r) => r.json()).then((d) => {
      if (d.error) setError(d.error); else { setCheck(d.check); setOptions(d.upsellOptions || []); }
    }).catch((e) => setError(String(e)));
  }, [checkRef, restaurantId]);
  return (
    <div style={wrap}>
      <h1 style={{ fontSize: 20 }}>Ваш заказ</h1>
      {error && <div style={{ ...card, borderColor: '#e5573f', color: '#b23' }}>{error}</div>}
      {check && <CheckCard check={check} />}
      {check && <UpsellButtons checkRef={check.checkRef} restaurantId={restaurantId} options={options} onUpdate={setCheck} />}
    </div>
  );
}

function Qr({ url }: { url: string }) {
  const [src, setSrc] = useState('');
  useEffect(() => { QRCode.toDataURL(url, { width: 180, margin: 1 }).then(setSrc).catch(() => {}); }, [url]);
  return (
    <div style={{ ...card, textAlign: 'center', marginBottom: 16 }}>
      <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>QR на дисплей — клиент сканит и докупает с телефона</div>
      {src && <img src={src} alt="QR" width={180} height={180} />}
      <div style={{ fontSize: 11, opacity: 0.5, wordBreak: 'break-all' }}>{url}</div>
    </div>
  );
}

function DemoConsole() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [check, setCheck] = useState<Check | null>(null);
  const [options, setOptions] = useState<MenuItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // sync
  const [restaurants, setRestaurants] = useState<{ id: string; name: string }[]>([]);
  const [restaurantId, setRestaurantId] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ total: number; created: number; updated: number; deactivated: number } | null>(null);
  // scenario B cart
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [cart, setCart] = useState<Record<number, number>>({});

  useEffect(() => {
    fetch(`${API_URL}/api/simphony-demo/config`).then((r) => r.json()).then((d) => setEnabled(!!d.enabled)).catch(() => setEnabled(false));
    fetch(`${API_URL}/api/simphony-demo/restaurants`).then((r) => r.json()).then((d) => {
      setRestaurants(d.restaurants || []);
      const withPos = (d.restaurants || []).find((r: any) => r.hasPos) || d.restaurants?.[0];
      if (withPos) setRestaurantId(withPos.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!restaurantId) { setMenu([]); return; }
    fetch(`${API_URL}/api/simphony-demo/menu?restaurantId=${restaurantId}`).then((r) => r.json()).then((d) => setMenu(d.items || [])).catch(() => setMenu([]));
  }, [restaurantId]);

  const syncMenu = async () => {
    if (!restaurantId) return;
    setSyncing(true); setError(''); setSyncResult(null);
    try {
      const r = await fetch(`${API_URL}/api/simphony-demo/sync`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Синк не прошёл');
      setSyncResult(d);
    } catch (e: any) { setError(e.message); } finally { setSyncing(false); }
  };

  const openAtPos = async () => { // Scenario A
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/simphony-demo/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Не удалось открыть чек');
      setCheck(d.check); setOptions(d.upsellOptions || []);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  const sendFromOlso = async () => { // Scenario B
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([id, q]) => ({ menuItemId: Number(id), quantity: q }));
    if (items.length === 0) return;
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/simphony-demo/order-from-olso`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restaurantId, items }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Не удалось отправить заказ');
      setCheck(d.check); setOptions(menu.filter((m) => m.price >= 200 && m.price <= 1500).slice(0, 4)); setCart({});
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  const finish = useCallback(async () => {
    if (!check) return;
    setBusy(true);
    try { await fetch(`${API_URL}/api/simphony-demo/${check.checkRef}?restaurantId=${restaurantId}`, { method: 'DELETE' }); } catch { /* ignore */ }
    setCheck(null); setOptions([]); setBusy(false);
  }, [check, restaurantId]);

  const cartCount = Object.values(cart).reduce((s, q) => s + q, 0);
  const qrUrl = check ? `${window.location.origin}/simphony-demo?check=${encodeURIComponent(check.checkRef)}&r=${encodeURIComponent(restaurantId)}` : '';

  return (
    <div style={wrap}>
      <h1 style={{ fontSize: 22 }}>OLSO × Burger King — живое демо</h1>
      <p style={{ opacity: 0.7, fontSize: 14 }}>Реальная тестовая касса BK (точка 998). Чеки удаляются по «Завершить».</p>

      {enabled === false && <div style={{ ...card, borderColor: '#e5573f' }}>Демо недоступно: на сервере не настроены доступы Simphony.</div>}
      {error && <div style={{ ...card, borderColor: '#e5573f', color: '#b23', marginBottom: 12 }}>{error}</div>}

      {enabled && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Синхронизация меню BK → товары OLSO</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} style={{ flex: 1, minWidth: 160, padding: 10, borderRadius: 10, border: '1px solid #e6ddcf' }}>
              {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <button style={{ ...btn, background: '#1f1b16', color: '#fff' }} disabled={syncing || !restaurantId} onClick={syncMenu}>{syncing ? 'Синкаем…' : 'Обновить меню'}</button>
          </div>
          {syncResult && <div style={{ marginTop: 10, fontSize: 14, color: '#2a6' }}>✓ {syncResult.total} позиций · создано {syncResult.created} · обновлено {syncResult.updated}</div>}
        </div>
      )}

      {enabled && !check && (
        <>
          {/* Scenario A */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Сценарий A — заказ на кассе → QR → апсел</div>
            <button style={{ ...btn, background: '#1f1b16', color: '#fff', width: '100%' }} disabled={busy} onClick={openAtPos}>{busy ? '…' : 'Открыть заказ на кассе BK'}</button>
          </div>
          {/* Scenario B */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Сценарий B — собрать заказ в OLSO → отправить в кассу</div>
            <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 10 }}>
              {menu.map((m) => (
                <div key={m.menuItemId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0e9dd' }}>
                  <span>{m.name} · ₸{m.price}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button style={{ ...btn, padding: '4px 10px', background: '#efe7da' }} onClick={() => setCart((c) => ({ ...c, [m.menuItemId]: Math.max(0, (c[m.menuItemId] || 0) - 1) }))}>−</button>
                    <span style={{ minWidth: 16, textAlign: 'center' }}>{cart[m.menuItemId] || 0}</span>
                    <button style={{ ...btn, padding: '4px 10px', background: '#efe7da' }} onClick={() => setCart((c) => ({ ...c, [m.menuItemId]: (c[m.menuItemId] || 0) + 1 }))}>+</button>
                  </span>
                </div>
              ))}
            </div>
            <button style={{ ...btn, background: cartCount ? '#1f1b16' : '#ccc', color: '#fff', width: '100%' }} disabled={busy || !cartCount} onClick={sendFromOlso}>Отправить заказ в кассу BK ({cartCount})</button>
          </div>
        </>
      )}

      {check && (
        <>
          <CheckCard check={check} />
          <Qr url={qrUrl} />
          <UpsellButtons checkRef={check.checkRef} restaurantId={restaurantId} options={options} onUpdate={setCheck} />
          <button style={{ ...btn, background: '#efe7da', color: '#2a2118', width: '100%', marginTop: 16 }} disabled={busy} onClick={finish}>Завершить и убрать тестовый чек</button>
        </>
      )}
    </div>
  );
}

export default function SimphonyDemoPage() {
  const params = new URLSearchParams(window.location.search);
  const checkRef = params.get('check');
  const r = params.get('r') || '';
  return checkRef ? <CustomerView checkRef={checkRef} restaurantId={r} /> : <DemoConsole />;
}
